import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '../../services/api';

// Category Visual Guidelines per GHF_Logo_and_Banner_Guidelines.docx
const categoryVisuals = {
  'sleep-bedtime': {
    mood: 'Calm evening tone',
    gradient: 'from-[#F0FDFA] to-[#E0F2FE]',
    iconColor: 'text-indigo-500',
    // Minimal moon icon
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  'behavior-emotions': {
    mood: 'Emotional grounding and clarity',
    gradient: 'from-[#FFF7ED] to-[#FEF2F2]',
    iconColor: 'text-rose-500',
    // Abstract balance icon
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M12 3v18M3 12h18M7 7l10 10M17 7L7 17" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
        <circle cx="12" cy="12" r="3" strokeLinecap="round"/>
      </svg>
    )
  },
  'screen-time-technology': {
    mood: 'Boundaries and control',
    gradient: 'from-[#F0F9FF] to-[#E0F2FE]',
    iconColor: 'text-cyan-600',
    // Minimal device with shield motif
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <rect x="5" y="4" width="14" height="12" rx="2" strokeLinecap="round"/>
        <path d="M12 16v4M8 20h8" strokeLinecap="round"/>
        <path d="M12 8v2M10 9h4" strokeLinecap="round" opacity="0.5"/>
      </svg>
    )
  },
  'family-bonding': {
    mood: 'Warm connection',
    gradient: 'from-[#FFFBEB] to-[#FEF3C7]',
    iconColor: 'text-amber-500',
    // Abstract family silhouette
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <circle cx="9" cy="7" r="2.5"/>
        <circle cx="15" cy="8" r="2"/>
        <path d="M5 21v-2a4 4 0 0 1 4-4h2" strokeLinecap="round"/>
        <path d="M15 21v-2a3 3 0 0 0-3-3" strokeLinecap="round"/>
        <path d="M19 21v-1a3 3 0 0 0-3-3h-1" strokeLinecap="round"/>
      </svg>
    )
  },
  'child-development': {
    mood: 'Growth and progression',
    gradient: 'from-[#ECFDF5] to-[#D1FAE5]',
    iconColor: 'text-emerald-500',
    // Subtle growth bar graphic
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M4 20h16" strokeLinecap="round"/>
        <rect x="6" y="14" width="3" height="6" rx="1" fill="currentColor" opacity="0.2"/>
        <rect x="10.5" y="10" width="3" height="10" rx="1" fill="currentColor" opacity="0.4"/>
        <rect x="15" y="6" width="3" height="14" rx="1" fill="currentColor" opacity="0.6"/>
      </svg>
    )
  },
};

const CoreProductGrid = ({ content }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const eyebrow = content?.eyebrow || 'Our Systems';
  const heading = content?.heading || 'Find the Right System for Your Family';
  const intro = content?.intro || 'Research-backed resources tailored to your biggest parenting challenges';
  const categoryOrder = Array.isArray(content?.category_order) ? content.category_order : [];

  const categoryOrderKey = categoryOrder.join(',');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.getCategories();
        let productCategories = data.filter(cat => cat.slug !== 'bundles-starter-kits');
        // Apply CMS category ordering when provided
        if (categoryOrder.length) {
          const order = new Map(categoryOrder.map((s, i) => [s, i]));
          productCategories = productCategories.slice().sort((a, b) => {
            const ai = order.has(a.slug) ? order.get(a.slug) : 999;
            const bi = order.has(b.slug) ? order.get(b.slug) : 999;
            return ai - bi;
          });
        }
        setCategories(productCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryOrderKey]);

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="wrap">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse">
                <div className="bg-[#E2E8F0] rounded-2xl aspect-square"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="core-product-grid">
      <div className="wrap">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-[#0F766E] font-semibold text-sm uppercase tracking-wider">{eyebrow}</span>
          <h2 className="text-3xl lg:text-[40px] font-bold mt-3 mb-4 text-[#0B1F3A]">
            {heading}
          </h2>
          <p className="text-[#475569] max-w-2xl mx-auto text-lg">
            {intro}
          </p>
        </div>

        {/* Category Grid - Using Visual Guidelines */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
          {categories.map((category) => {
            const visuals = categoryVisuals[category.slug] || categoryVisuals['sleep-bedtime'];
            
            return (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group"
                data-testid={`category-card-${category.slug}`}
              >
                <div className={`bg-gradient-to-br ${visuals.gradient} rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 border border-white/50 hover:-translate-y-1 h-full flex flex-col`}>
                  {/* Minimal Line Icon per Visual Standards */}
                  <div className={`w-16 h-16 rounded-2xl bg-white/80 flex items-center justify-center mx-auto mb-4 ${visuals.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                    {visuals.icon}
                  </div>
                  
                  {/* Title */}
                  <h3 className="font-bold text-[#0B1F3A] mb-2 group-hover:text-[#0F766E] transition-colors">
                    {category.name}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-[#475569] flex-grow line-clamp-2 mb-3">
                    {category.description}
                  </p>
                  
                  {/* Arrow */}
                  <div className="flex items-center justify-center gap-1 text-[#0F766E] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Explore</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* View All Link */}
        <div className="text-center mt-10">
          <Link 
            to="/shop"
            className="inline-flex items-center gap-2 bg-white text-[#0B1F3A] font-semibold px-6 py-3 rounded-full border border-[#E2E8F0] hover:border-[#0F766E]/30 hover:text-[#0F766E] transition-all"
            data-testid="view-all-products-link"
          >
            View All Products
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CoreProductGrid;
